import React from "react";

import { homeURL } from "Dashboard/EmployeeUrls";
import { StepName } from "EmployeeApplication/actions";
import AdditionalInformationPage from "EmployeeApplication/AdditionalInformationPage";
import { useGetApplicationQuery, getAdminMode, getAvailablePlans, getBenefitsAdmin } from "EmployeeApplication/api";
import BasicInformationPage from "EmployeeApplication/BasicInformationPage";
import ChooseAccidentCoveragePage from "EmployeeApplication/ChooseAccidentCoveragePage";
import ChooseCoveragePage from "EmployeeApplication/ChooseCoveragePage";
import ChooseLifeCoveragePage from "EmployeeApplication/ChooseLifeCoveragePage";
import ChooseLongTermDisabilityCoveragePage from "EmployeeApplication/ChooseLongTermDisabilityCoveragePage";
import ChooseShortTermDisabilityCoveragePage from "EmployeeApplication/ChooseShortTermDisabilityCoveragePage";
import ChooseTermLifeCoveragePage from "EmployeeApplication/ChooseTermLifeCoveragePage";
import ChooseWholeLifeCoveragePage from "EmployeeApplication/ChooseWholeLifeCoveragePage";
import FamilyInformationPage from "EmployeeApplication/FamilyInformationPage";
import Page from "EmployeeApplication/Page";
import ChooseMedicalCoveragePage from "EmployeeApplication/pages/ChooseMedicalCoveragePage";
import { getEmployeeHashId } from "EmployeeApplication/selectors";
import SignaturePage from "EmployeeApplication/SignaturePage";
import {
  adminEmployeeParam,
  basicInformationURL,
  familyInformationURL,
  chooseMedicalPlanURL,
  chooseDentalPlanURL,
  chooseVisionPlanURL,
  chooseTermLifePlanURL,
  signURL,
  doneURL,
  nestedEmployeeRenewalApplicationRoutes,
  additionalInformationURL,
  chooseShortTermDisabilityPlanURL,
  chooseLongTermDisabilityPlanURL,
  chooseAccidentPlanURL,
  chooseLifePlanURL,
  chooseWholeLifePlanURL,
} from "EmployeeApplication/urls";
import isIntuit from "Intuit/isIntuit";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { Switch, Route, Link } from "react-router-dom";
import isSquare from "Square/isSquare";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { companyTokenString } from "Utils/companyToken";
import { routerUrlHelper } from "Utils/routerUrl";
import { BasicLoading, GenericErrorPage } from "ZeroRuby";
import Heartbeat from "ZeroRuby/snippets/heartbeat";

import { withErrorHandler } from "Cobalt/ErrorHandler";
import { NavItem } from "Cobalt/NavItem";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { CardHeader, CardSection } from "Common/Card";
import Footer from "Common/Footer";
import { RoutedBackButton } from "Common/forms/buttons";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import ScrollToTop from "Common/ScrollToTop";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import ResponsiveTopBar from "Common/TopBar/ResponsiveTopBar";

import ProcessOverview from "./ProcessOverview";

const DonePage = () => {
  const { isLoading } = useGetApplicationQuery();
  const employeeHashId = useSelector(getEmployeeHashId);
  const isAdminMode = useSelector(getAdminMode);
  const benefitsAdmin = useSelector(getBenefitsAdmin);
  const companyIDToken = companyTokenString();
  const employerRenewalURL = benefitsAdmin ? "/benefits-administration/renewal/status" : "/employer-renewal/employee-status";
  const returnURL = `${employerRenewalURL}${companyIDToken ? `?company_id_token=${companyIDToken}` : ""}`;

  return (
    <Page title="You are done">
      {isLoading ? (
        <BasicLoading />
      ) : (
        <>
          <CardHeader>
            You are done! <Icon icon="smile" />
          </CardHeader>
          <CardSection>
            <BodyText componentClass="p" size="large" agent>
              Our team will be in touch once your application is approved.
            </BodyText>
            {!(isSquare() || isIntuit()) && (
              <BodyText componentClass="p" size="large" agent>
                If you have questions in the meantime please check out our{" "}
                <a href="https://support.simplyinsured.com/s/topic/0TO3t000000auQuGAI/simplyinsured-for-employees">FAQ for Employees</a>.
              </BodyText>
            )}
          </CardSection>
          <CardSection className="clearfix">
            <RoutedBackButton href={signURL(isAdminMode, true, employeeHashId, false)} />
            {!isAdminMode && (
              <Button component={Link} size="medium" className="float-right" to={homeURL(isAdminMode, employeeHashId)}>
                Back to Dashboard
              </Button>
            )}
            {isAdminMode && (
              <Button size="medium" style={{ float: "right" }} className="float-right" href={appendAuthParams(routerUrlHelper(returnURL))}>
                Exit Employee Application
              </Button>
            )}
          </CardSection>
        </>
      )}
    </Page>
  );
};

const JumpPage = ({ isAdminMode, employeeFormId }) => (
  <Page title="Jump to step">
    <ul>
      <li>
        <Link to={basicInformationURL(isAdminMode, true, employeeFormId, false)}>Basic Information</Link>
      </li>
      <li>
        <Link to={familyInformationURL(isAdminMode, true, employeeFormId, false)}>Family Information</Link>
      </li>
      <li>
        <Link to={chooseMedicalPlanURL(isAdminMode, true, employeeFormId, false)}>Choose Medical Plan</Link>
      </li>
      <li>
        <Link to={chooseDentalPlanURL(isAdminMode, true, employeeFormId, false)}>Choose Dental Plan</Link>
      </li>
      <li>
        <Link to={chooseVisionPlanURL(isAdminMode, true, employeeFormId, false)}>Choose Vision Plan</Link>
      </li>
      <li>
        <Link to={chooseTermLifePlanURL(isAdminMode, true, employeeFormId, false)}>Choose Term Life Plan</Link>
      </li>
      <li>
        <Link to={chooseShortTermDisabilityPlanURL(isAdminMode, false, employeeFormId, false)}>Choose Short Term Disability Plan</Link>
      </li>
      <li>
        <Link to={chooseLongTermDisabilityPlanURL(isAdminMode, false, employeeFormId, false)}>Choose Long Term Disability Plan</Link>
      </li>
      <li>
        <Link to={signURL(isAdminMode, true, employeeFormId, false)}>Signature</Link>
      </li>
      <li>
        <Link to={doneURL(isAdminMode, true, employeeFormId, false)}>Done</Link>
      </li>
    </ul>
  </Page>
);

const getPreviousStep = (steps, previousStepIndex) => {
  if (previousStepIndex < 0) {
    throw new Error(`No previous step: ${JSON.stringify(previousStepIndex)}`);
  }
  const previousStep = steps[previousStepIndex];
  if (previousStep.url === null) {
    return getPreviousStep(steps, previousStepIndex - 1);
  }
  return previousStep;
};

const backURL = (
  stepName: StepName,
  adminMode: boolean,
  employeeFormId: string,
  availablePlans: Array<"medical" | "dental" | "vision" | "accident" | "life" | "term_life" | "whole_life" | "long_term_disability" | "short_term_disability">,
  isRenewal: boolean,
): string => {
  const steps = [
    { name: "basicInformation", url: basicInformationURL(adminMode, isRenewal, employeeFormId, false) },
    { name: "familyInformation", url: familyInformationURL(adminMode, isRenewal, employeeFormId, false) },
    { name: "chooseMedicalPlan", url: availablePlans.includes("medical") ? chooseMedicalPlanURL(adminMode, isRenewal, employeeFormId, false) : null },
    { name: "chooseDentalPlan", url: availablePlans.includes("dental") ? chooseDentalPlanURL(adminMode, isRenewal, employeeFormId, false) : null },
    { name: "chooseVisionPlan", url: availablePlans.includes("vision") ? chooseVisionPlanURL(adminMode, isRenewal, employeeFormId, false) : null },
    { name: "chooseAccidentPlan", url: availablePlans.includes("accident") ? chooseAccidentPlanURL(adminMode, isRenewal, employeeFormId, false) : null },
    { name: "chooseLifePlan", url: availablePlans.includes("life") ? chooseLifePlanURL(adminMode, isRenewal, employeeFormId, false) : null },
    { name: "chooseTermLifePlan", url: availablePlans.includes("term_life") ? chooseTermLifePlanURL(adminMode, isRenewal, employeeFormId, false) : null },
    { name: "chooseWholeLifePlan", url: availablePlans.includes("whole_life") ? chooseWholeLifePlanURL(adminMode, isRenewal, employeeFormId, false) : null },
    {
      name: "chooseShortTermDisabilityPlan",
      url: availablePlans.includes("short_term_disability") ? chooseShortTermDisabilityPlanURL(adminMode, isRenewal, employeeFormId, false) : null,
    },
    {
      name: "chooseLongTermDisabilityPlan",
      url: availablePlans.includes("long_term_disability") ? chooseLongTermDisabilityPlanURL(adminMode, isRenewal, employeeFormId, false) : null,
    },
    { name: "additionalInformation", url: additionalInformationURL(adminMode, isRenewal, employeeFormId, false) },
    { name: "sign", url: signURL(adminMode, isRenewal, employeeFormId, false) },
    { name: "done", url: doneURL(adminMode, isRenewal, employeeFormId, false) },
  ];
  const currentStepIndex = steps.findIndex((s) => s.name === stepName);
  if (currentStepIndex === null) {
    throw new Error(`Unknown step: ${JSON.stringify(stepName)}`);
  }

  const previousStepIndex = currentStepIndex - 1;
  if (previousStepIndex < 0) {
    throw new Error(`No previous step: ${JSON.stringify(stepName)}`);
  }
  const previousStep = getPreviousStep(steps, previousStepIndex);
  return previousStep.url;
};

const NotFoundPage = () => (
  <Page title="Whoops! Bad Link!">
    <BodyText componentClass="p" size="large" agent>
      You've clicked on a link we don't know how to handle. Please contact support for assistance.
    </BodyText>
  </Page>
);

const rightContent = [
  isIntuit() && (
    <NavItem href="/logout">
      <Icon icon="sign-out-alt" flavor="solid" mx={3} />
      Log out
    </NavItem>
  ),
];

const EmployeeRenewalApplication = ({ match }) => {
  const isAdminMode = useSelector(getAdminMode);
  const employeeFormId = match.params[adminEmployeeParam];
  const { isLoading } = useGetApplicationQuery();
  const availablePlans = useSelector(getAvailablePlans);

  return (
    <div>
      <Helmet titleTemplate="SimplyInsured - %s" defaultTitle="SimplyInsured" />
      <ResponsiveTopBar rightContent={rightContent} />

      <ProcessOverview />
      <Div className="min-h-dvh">
        {isLoading ? (
          <BasicLoading />
        ) : (
          <ScrollToTop>
            <Switch>
              <Route path={nestedEmployeeRenewalApplicationRoutes("/")} exact>
                <BasicInformationPage isRenewal availablePlans={availablePlans} />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/family-information")} exact>
                <FamilyInformationPage
                  backLink={backURL("familyInformation", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "family_information_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-medical-coverage")} exact>
                <ChooseMedicalCoveragePage
                  backLink={backURL("chooseMedicalPlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_medical_coverage_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-dental-coverage")} exact>
                <ChooseCoveragePage
                  type="dental"
                  backLink={backURL("chooseDentalPlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_dental_coverage_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-vision-coverage")} exact>
                <ChooseCoveragePage
                  type="vision"
                  backLink={backURL("chooseVisionPlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_vision_coverage_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-accident-coverage")} exact>
                <ChooseAccidentCoveragePage
                  backLink={backURL("chooseAccidentPlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_accident_coverage_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-life-coverage")} exact>
                <ChooseLifeCoveragePage
                  backLink={backURL("chooseLifePlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_life_coverage_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-term-life-coverage")} exact>
                <ChooseTermLifeCoveragePage
                  backLink={backURL("chooseTermLifePlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_term_life_coverage_page",
                    });
                  }}
                />
              </Route>
              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-whole-life-coverage")} exact>
                <ChooseWholeLifeCoveragePage
                  backLink={backURL("chooseWholeLifePlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_whole_life_coverage_page",
                    });
                  }}
                />
              </Route>
              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-short-term-disability-coverage")} exact>
                <ChooseShortTermDisabilityCoveragePage
                  backLink={backURL("chooseShortTermDisabilityPlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_short_term_disability_coverage_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/choose-long-term-disability-coverage")} exact>
                <ChooseLongTermDisabilityCoveragePage
                  backLink={backURL("chooseLongTermDisabilityPlan", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "choose_long_term_disability_coverage_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/additional-information")} exact>
                <AdditionalInformationPage
                  backLink={backURL("additionalInformation", isAdminMode, employeeFormId, availablePlans, true)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "additional_information_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/sign")} exact>
                <SignaturePage
                  backLink={backURL("sign", isAdminMode, employeeFormId, availablePlans, true)}
                  basicInfoLink={basicInformationURL(isAdminMode, true, employeeFormId, false)}
                  onClickBack={() => {
                    AnalyticsTracking.trackEvent("back_button_clicked", {
                      application: "employee_renewal_application",
                      location: "signature_page",
                    });
                  }}
                />
              </Route>

              <Route path={nestedEmployeeRenewalApplicationRoutes("/done")} exact>
                <DonePage />
              </Route>
              <Route path={nestedEmployeeRenewalApplicationRoutes("/jump")} exact>
                <JumpPage isAdminMode={isAdminMode} employeeFormId={employeeFormId} />
              </Route>
              <Route>
                <NotFoundPage />
              </Route>
            </Switch>
          </ScrollToTop>
        )}
      </Div>
      <Footer />
      <Heartbeat />
    </div>
  );
};

/** In cases of unexpected errors, am error page is the fallback and the issue is reported to HoneyBadger */
const EmployeeRenewalApplicationWithErrorHandler = withErrorHandler(EmployeeRenewalApplication, {
  component: GenericErrorPage,
  cta: "Back to Dashboard",
  href: "/employee-dashboard",
});

// Finally, export the wrapped component.
export default EmployeeRenewalApplicationWithErrorHandler;
